import { ROUTE_PATHS } from '@/constants';
import type { AuthData } from '@/types/auth';
import type { QueryClient } from '@tanstack/react-query';
import { ErrorComponent, createBrowserHistory, createRouter } from '@tanstack/react-router';
import React from 'react';

import { routeTree } from '../routeTree.gen';

import { queryClient } from './query-client';
import { setUnauthorizedHandler } from './unauthorized-handler';

type RouterContext = {
  authentication: AuthData;
  queryClient: QueryClient;
};

// Create the history instance first
const history = createBrowserHistory();

// Create a NavigationManager that we'll use before router is initialized
class NavigationManager {
  private static instance: NavigationManager;
  private routerInstance: typeof router | null = null;

  private constructor() {}

  static getInstance(): NavigationManager {
    if (!NavigationManager.instance) {
      NavigationManager.instance = new NavigationManager();
    }
    return NavigationManager.instance;
  }

  setRouter(r: typeof router) {
    this.routerInstance = r;
  }

  navigate(to: string, search?: Record<string, string>) {
    if (this.routerInstance) {
      // Use the router's history API which doesn't have the strict typing
      const searchParams = search ? `?${new URLSearchParams(search).toString()}` : '';
      this.routerInstance.history.push(`${to}${searchParams}`);
    } else {
      // Fallback if router isn't initialized yet
      const searchParams = search ? `?${new URLSearchParams(search).toString()}` : '';
      window.location.href = `${to}${searchParams}`;
    }
  }
}

const navigationManager = NavigationManager.getInstance();

// Define handleUnauthorized locally and register it globally
const handleUnauthorized = () => {
  if (window.location.pathname !== ROUTE_PATHS.LOGIN && window.location.pathname !== ROUTE_PATHS.RESET_PASSWORD) {
    navigationManager.navigate(ROUTE_PATHS.LOGIN, {
      redirect: window.location.pathname,
    });
  }
};

// Register handler with the unauthorized-handler module (used by api-client, useAuthStatus, and __root)
setUnauthorizedHandler(handleUnauthorized);

// Create the router with the queryClient
const router = createRouter({
  context: {
    authentication: null as unknown as AuthData,
    queryClient,
  },
  defaultErrorComponent: ({ error }) => <ErrorComponent error={error} />,
  defaultPreload: 'intent',
  defaultStaleTime: 5000,
  defaultPreloadStaleTime: 0,
  defaultStructuralSharing: true,
  defaultViewTransition: true,
  history,
  routeTree,
  scrollRestoration: true,
  scrollRestorationBehavior: 'instant',
  scrollToTopSelectors: ['#main-scrollable-area'],
});

// Set the router instance in our navigation manager
navigationManager.setRouter(router);

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

export { router };
export type { RouterContext };
